import { useEffect, useRef, useState } from "react";
import { Eraser, Check } from "lucide-react";

/**
 * SignaturePad - lienzo HTML canvas para captura de firma.
 * - Devuelve un dataURL (PNG) vía onChange cuando el usuario suelta el trazo.
 * - Soporta puntero (mouse + touch + pen) gracias a Pointer Events.
 * - Prop `value` permite hidratar firmas previas (renderiza la imagen al fondo).
 */
export default function SignaturePad({ value, onChange, label, testId = "signature-pad", height = 140 }) {
    const canvasRef = useRef(null);
    const ctxRef = useRef(null);
    const drawing = useRef(false);
    const lastPt = useRef(null);
    const [hasInk, setHasInk] = useState(Boolean(value));

    // Configurar canvas en alta resolución
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        canvas.width = rect.width * dpr;
        canvas.height = height * dpr;
        const ctx = canvas.getContext("2d");
        ctx.scale(dpr, dpr);
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.lineWidth = 2;
        ctx.strokeStyle = "#0f172a";
        ctxRef.current = ctx;

        // Hidratar si hay value
        if (value && value.startsWith("data:image")) {
            const img = new Image();
            img.onload = () => ctx.drawImage(img, 0, 0, rect.width, height);
            img.src = value;
            setHasInk(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getPt = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const start = (e) => {
        e.preventDefault();
        canvasRef.current.setPointerCapture?.(e.pointerId);
        drawing.current = true;
        lastPt.current = getPt(e);
        setHasInk(true);
    };

    const move = (e) => {
        if (!drawing.current) return;
        const pt = getPt(e);
        const ctx = ctxRef.current;
        ctx.beginPath();
        ctx.moveTo(lastPt.current.x, lastPt.current.y);
        ctx.lineTo(pt.x, pt.y);
        ctx.stroke();
        lastPt.current = pt;
    };

    const end = () => {
        if (!drawing.current) return;
        drawing.current = false;
        const data = canvasRef.current.toDataURL("image/png");
        onChange?.(data);
    };

    const clear = () => {
        const ctx = ctxRef.current;
        const canvas = canvasRef.current;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasInk(false);
        onChange?.("");
    };

    return (
        <div data-testid={testId} className="space-y-1">
            {label && <label className="fm-label flex items-center justify-between">
                <span>{label}</span>
                {hasInk && <span className="text-emerald-600 inline-flex items-center gap-1 text-[10px]"><Check className="w-3 h-3" /> Capturada</span>}
            </label>}
            <div className="relative border border-slate-300 rounded-sm bg-white overflow-hidden">
                <canvas
                    ref={canvasRef}
                    style={{ width: "100%", height: `${height}px`, touchAction: "none", cursor: "crosshair" }}
                    onPointerDown={start}
                    onPointerMove={move}
                    onPointerUp={end}
                    onPointerLeave={end}
                    data-testid={`${testId}-canvas`}
                />
                {!hasInk && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-slate-300 text-xs font-mono uppercase tracking-widest select-none">
                        Firmar aquí
                    </div>
                )}
            </div>
            <button
                type="button"
                onClick={clear}
                data-testid={`${testId}-clear`}
                className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-red-600 inline-flex items-center gap-1"
            >
                <Eraser className="w-3 h-3" /> Limpiar
            </button>
        </div>
    );
}
